import { useRef, useState } from 'react';
import {
  ActionIcon,
  Button,
  Card,
  Center,
  Group,
  Image,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCamera, IconDoorExit, IconFileText, IconKeyboard } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { ApiError, unwrap } from '../api/client';
import { startSession, startSheetSession, startTextSession } from '../api/gen';
import { ChildAvatar } from '../components/ChildAvatar';
import { PuzzleCard } from '../components/PuzzleCard';
import { SheetPicker } from '../components/SheetPicker';
import { useOrg, useSession } from '../lib/session';
import { accent } from '../theme';

// Where a child starts: a photograph of the worksheet, a printed Sokrates
// sheet they have in front of them, or the thing they are stuck on, typed.
//
// The camera is the front door. `capture="environment"` on a file input opens
// the rear camera on a phone or tablet and the file picker everywhere else,
// which is the whole of the platform handling this needs.
export function StartPage() {
  const { who, leave } = useSession();
  const org = useOrg();
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [question, setQuestion] = useState('');
  const [typing, setTyping] = useState(false);
  const [picking, setPicking] = useState(false);

  const child = who.kind === 'child' ? who : null;
  const a = accent(child?.mode ?? 'junior');

  const fail = (err: unknown) => {
    const message =
      err instanceof ApiError && err.status === 402
        ? 'Für neue Sitzungen braucht die Familie ein Abo. Der Lernverlauf bleibt sichtbar.'
        : err instanceof ApiError && err.status === 503
          ? 'Sokrates ist gerade nicht erreichbar. Versuch es gleich noch einmal.'
          : (err as Error).message;
    notifications.show({ color: 'red', message });
  };

  const fromPhoto = useMutation({
    mutationFn: async (file: File) =>
      unwrap(
        startSession({
          query: { childId: child!.childId },
          headers: org,
          body: { worksheet: file },
        }),
      ),
    onSuccess: (view) => view && navigate(`/sitzung/${view.session.id}`),
    onError: fail,
  });

  const fromQuestion = useMutation({
    mutationFn: async (text: string) =>
      unwrap(
        startTextSession({ headers: org, body: { childId: child!.childId, question: text } }),
      ),
    onSuccess: (view) => view && navigate(`/sitzung/${view.session.id}`),
    onError: fail,
  });

  const fromSheet = useMutation({
    mutationFn: async (sheet: string) =>
      unwrap(startSheetSession({ headers: org, body: { childId: child!.childId, sheet } })),
    onSuccess: (view) => view && navigate(`/sitzung/${view.session.id}`),
    onError: fail,
  });

  if (!child) return null;
  const busy = fromPhoto.isPending || fromQuestion.isPending || fromSheet.isPending;

  return (
    <Center mih="100vh" p="md" style={{ background: a.soft }}>
      <Stack w={480} maw="100%">
        <Group justify="space-between">
          <Group gap="sm">
            <ChildAvatar name={child.name} mode={child.mode} size={40} />
            <Title order={3}>Hallo {child.name}</Title>
          </Group>
          <ActionIcon variant="subtle" size="lg" aria-label="Abmelden" onClick={leave}>
            <IconDoorExit size={20} />
          </ActionIcon>
        </Group>

        <Card withBorder padding="lg" radius="lg">
          <Stack align="center" gap="md">
            {preview ? (
              <Image src={preview} alt="Dein Arbeitsblatt" radius="md" mah={280} fit="contain" />
            ) : (
              <Text c="dimmed" size="sm" ta="center">
                Fotografiere dein Arbeitsblatt. Sokrates schaut es sich an und fragt dich,
                womit du anfangen möchtest.
              </Text>
            )}

            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              onChange={(e) => {
                const file = e.currentTarget.files?.[0];
                if (!file) return;
                setPreview(URL.createObjectURL(file));
                fromPhoto.mutate(file);
              }}
            />

            <Button
              size="lg"
              radius="xl"
              color={a.color}
              leftSection={<IconCamera size={22} />}
              loading={fromPhoto.isPending}
              disabled={busy}
              onClick={() => fileRef.current?.click()}
            >
              Arbeitsblatt fotografieren
            </Button>
          </Stack>
        </Card>

        <Button
          variant="light"
          size="md"
          radius="xl"
          color={a.color}
          leftSection={<IconFileText size={20} />}
          loading={fromSheet.isPending}
          disabled={busy}
          onClick={() => setPicking(true)}
        >
          Ich habe ein Blatt von Sokrates
        </Button>
        <SheetPicker
          opened={picking}
          color={a.color}
          onClose={() => setPicking(false)}
          onPick={(sheet) => {
            setPicking(false);
            fromSheet.mutate(sheet.id);
          }}
        />

        {typing ? (
          <Card withBorder padding="lg" radius="lg">
            <Stack>
              <Textarea
                autosize
                minRows={2}
                maxRows={6}
                label="Woran hängst du?"
                placeholder="Wie teile ich 35 durch 5?"
                value={question}
                onChange={(e) => setQuestion(e.currentTarget.value)}
              />
              <Group justify="flex-end">
                <Button variant="subtle" onClick={() => setTyping(false)} disabled={busy}>
                  Abbrechen
                </Button>
                <Button
                  color={a.color}
                  disabled={!question.trim() || busy}
                  loading={fromQuestion.isPending}
                  onClick={() => fromQuestion.mutate(question.trim())}
                >
                  Los
                </Button>
              </Group>
            </Stack>
          </Card>
        ) : (
          <Button
            variant="subtle"
            leftSection={<IconKeyboard size={18} />}
            onClick={() => setTyping(true)}
            disabled={busy}
          >
            Lieber tippen
          </Button>
        )}

        <PuzzleCard childId={child.childId} color={a.color} />
      </Stack>
    </Center>
  );
}
